import {
    Box,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlineRoundedIcon from "@mui/icons-material/InfoOutlineRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { useCurrencyConversion } from "shared/contexts/CurrencyConversionContext";
import { formatSignedCurrencyAmountOrMasked } from "features/wallet/lib/walletDisplay";
import type { WalletAccountSection } from "features/wallet/lib/walletDisplay";
import { formatCurrencyAmount } from "shared/money/balances";

export interface WalletTotalCardProps {
    estimatedTotal: number;
    displayCurrency: string;
    sections: WalletAccountSection[];
    balancesVisible: boolean;
    locale: string;
    onToggleBalances: () => void;
    infoLabel: string;
    hideBalancesLabel: string;
    showBalancesLabel: string;
    title: string;
    variant?: "default" | "compact";
}

export default function WalletTotalCard({
    estimatedTotal,
    displayCurrency,
    sections,
    balancesVisible,
    locale,
    onToggleBalances,
    infoLabel,
    hideBalancesLabel,
    showBalancesLabel,
    title,
    variant = "default",
}: WalletTotalCardProps) {
    const { convertBalances, engine } = useCurrencyConversion();
    const totalLabel = formatSignedCurrencyAmountOrMasked(estimatedTotal, displayCurrency, balancesVisible, locale);
    const isCompact = variant === "compact";
    const currencies = Array.from(new Set(sections.flatMap((section) => Object.keys(section.balancesByCurrency))));
    const conversionEquations = currencies.flatMap((currency) => {
        if (currency === displayCurrency) return [];
        const unitsPerDisplay = engine.currentRates[currency];
        if (!Number.isFinite(unitsPerDisplay) || unitsPerDisplay <= 0) return [];
        return [`1 ${currency} = ${formatCurrencyAmount(1 / unitsPerDisplay, displayCurrency, locale)}`];
    });
    const conversionTooltip = (
        <Box>
            <Typography variant="body2">{infoLabel}</Typography>
            {conversionEquations.map((equation) => <Typography key={equation} variant="caption" component="div">{equation}</Typography>)}
        </Box>
    );

    return (
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                overflow: "hidden",
                p: isCompact ? 1.75 : { xs: 2.25, sm: 2.75 },
                borderRadius: isCompact ? 3 : 4,
                bgcolor: "background.paper",
                backgroundImage: (theme) => theme.palette.mode === "dark"
                    ? `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.24)}, ${alpha(theme.palette.background.paper, 0.94)})`
                    : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.background.paper, 0.99)})`,
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.16)}`,
                boxShadow: (theme) => theme.palette.mode === "dark"
                    ? `0 18px 44px ${alpha(theme.palette.common.black, 0.28)}`
                    : `0 18px 40px ${alpha(theme.palette.common.black, 0.08)}`,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
                        <Typography variant={isCompact ? "subtitle2" : "body1"} sx={{ fontWeight: 800 }}>
                            {title}
                        </Typography>
                        <Tooltip title={conversionTooltip}>
                            <IconButton size="small" aria-label={infoLabel} sx={{ p: 0.25, color: "text.secondary" }}>
                                <InfoOutlineRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Tooltip title={balancesVisible ? hideBalancesLabel : showBalancesLabel}>
                    <IconButton
                        size={isCompact ? "small" : "medium"}
                        onClick={onToggleBalances}
                        aria-label={balancesVisible ? hideBalancesLabel : showBalancesLabel}
                        sx={{
                            width: isCompact ? 32 : { xs: 48, sm: 56 },
                            height: isCompact ? 32 : { xs: 48, sm: 56 },
                            flexShrink: 0,
                            borderRadius: isCompact ? 2 : 3,
                            color: "primary.main",
                            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.1),
                            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            "& .MuiSvgIcon-root": { fontSize: isCompact ? 18 : undefined },
                        }}
                    >
                        {balancesVisible ? <VisibilityRoundedIcon /> : <VisibilityOffRoundedIcon />}
                    </IconButton>
                </Tooltip>
            </Box>

            <Typography
                sx={{
                    mt: 1,
                    color: estimatedTotal < 0 ? "error.main" : "text.primary",
                    fontSize: isCompact ? { xs: 20, sm: 24 } : { xs: 24, sm: 32 },
                    lineHeight: 1.05,
                    fontWeight: 900,
                    letterSpacing: 0,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {totalLabel}
            </Typography>

            {sections.length > 0 && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: isCompact
                            ? `repeat(${Math.min(sections.length, 2)}, minmax(0, 1fr))`
                            : { xs: "1fr 1fr", sm: `repeat(${Math.min(sections.length, 3)}, minmax(0, 1fr))` },
                        gap: isCompact ? 1 : 1.5,
                        mt: isCompact ? 1.5 : 2.25,
                        pt: isCompact ? 1.25 : 2,
                        borderTop: (theme) => `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.1 : 0.08)}`,
                    }}
                >
                    {sections.slice(0, 3).map((section) => {
                        const displayCurrencyForSection = section.key === "USD" ? "USD" : section.key;
                        const total = convertBalances(section.balancesByCurrency, {
                            toCurrency: displayCurrencyForSection,
                        });
                        const color = total < -0.01 ? "error.main" : total > 0.01 ? "success.main" : "text.secondary";
                        const labelColor = isCompact ? "text.secondary" : color;
                        return (
                            <Box key={section.key} sx={{ minWidth: 0 }}>
                                <Typography variant="caption" sx={{ display: "block", color: labelColor, fontWeight: isCompact ? 700 : 900 }}>
                                    {section.key}
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 0.25,
                                        color,
                                        fontWeight: isCompact ? 700 : 900,
                                        fontSize: isCompact ? { xs: 14, sm: 15 } : { xs: 17, sm: 20 },
                                        lineHeight: 1.15,
                                        whiteSpace: "normal",
                                        overflowWrap: "anywhere",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {formatSignedCurrencyAmountOrMasked(total, displayCurrencyForSection, balancesVisible, locale)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Paper>
    );
}
